import { z } from 'zod'
import { nullableCalendarDaySchema } from '../validators/calendar-day.validator.js'

/**
 * One event plan a change would reach, as a confirmation names it.
 *
 * Built to be read by the person about to make the change: which event, which
 * day, which plan, how many pieces on it are affected, and who has it open.
 */
export const impactedPlanSchema = z.object({
  eventId: z.string().uuid(),
  eventName: z.string(),
  eventDate: nullableCalendarDaySchema,
  phaseId: z.string().uuid(),
  phaseLabel: z.string(),
  /** Confirmed, canceled or finished — a plan that keeps what it was settled on. */
  settled: z.boolean(),
  /**
   * The pieces on the plan this change affects: those that would sit outside a
   * smaller room, be short of stock, or be taken off. Zero when the change
   * reaches the plan as a whole rather than particular pieces.
   */
  pieces: z.number().int().nonnegative(),
  /** Who has the plan open right now, by name. */
  editingNow: z.array(z.string()),
})

export type ImpactedPlan = z.infer<typeof impactedPlanSchema>

/**
 * What a change to something plans depend on would do to them, before it is
 * made — for a confirmation that can say "this changes 4 unconfirmed plans"
 * rather than asking "are you sure?".
 */
export const planImpactDtoSchema = z.object({
  /** Plans that will show the change. */
  plans: z.array(impactedPlanSchema),
  /** Settled plans that keep things as they were, untouched by the change. */
  settledKept: z.number().int().nonnegative(),
  /** Settled plans that stop the change from being made at all. */
  blocking: z.array(impactedPlanSchema),
})

export type PlanImpactDto = z.infer<typeof planImpactDtoSchema>

/** One event renting a vendor's piece, as the vendor sees it — which venue and day, not whose event. */
export const vendorRentalUseSchema = z.object({
  venueName: z.string(),
  eventDate: nullableCalendarDaySchema,
  quantity: z.number().int().nonnegative(),
})

export type VendorRentalUse = z.infer<typeof vendorRentalUseSchema>

/**
 * Where a furniture piece is in use, for the editor to say before a change
 * that reaches all of it — a new category, a seat count, a shape, its stock.
 */
export const furnitureUsageDtoSchema = z.object({
  /** Event plans with the piece placed; settled ones marked. */
  plans: z.array(impactedPlanSchema),
  /** Saved venue layouts it is in. */
  layouts: z.number().int().nonnegative(),
  /** Furniture arrangements (groups) it is a member of. */
  arrangements: z.number().int().nonnegative(),
  /** For a vendor's piece: the upcoming events renting it. */
  rentals: z.array(vendorRentalUseSchema),
})

export type FurnitureUsageDto = z.infer<typeof furnitureUsageDtoSchema>

/**
 * Who has each of an event's plans open right now, for a confirmation that is
 * about to take a plan away from them — deleting the plan, or the event.
 * The caller is left out, and so are plans nobody else has open.
 */
export const eventEditorsDtoSchema = z.object({
  plans: z.array(
    z.object({
      phaseId: z.string().uuid(),
      names: z.array(z.string()),
    }),
  ),
})

export type EventEditorsDto = z.infer<typeof eventEditorsDtoSchema>
